import {
  div,
  h1,
  h2,
  span,
  button,
  select,
  option,
  fnstate,
  goTo,
  progress,
  input
} from '@srfnstack/fntags'
import { eventBus } from '../state.js'

const PayloadAnalyzer = (payload) => {
  if (!payload || typeof payload !== 'object') return null

  const ruleMap = {
    concept_prompt_id: (id) => ({
      label: `Artwork Manager #${id}`,
      to: `/artwork-review/${id}`,
      icon: 'palette'
    }),
    design_seed_id: (id) => ({
      label: `Design Seed #${id}`,
      to: `/design-seeds/${id}`,
      icon: 'trending_up'
    }),
    product_concept_id: (id) => ({
      label: `Product Review #${id}`,
      to: `/product-review?id=${id}`,
      icon: 'shopping_bag'
    }),
    source_asset_id: (id) => ({
      label: `View Asset #${id}`,
      onclick: async () => {
        const res = await fetch(`/api/assets/${id}`)
        if (res.ok) {
          const asset = await res.json()
          goTo(`/artwork-review/${asset.concept_prompt_id}?assetId=${id}`)
        }
      },
      icon: 'image'
    })
  }

  const links = Object.entries(payload)
    .filter(([key]) => ruleMap[key])
    .map(([key, value]) => ruleMap[key](value))

  if (links.length === 0) return null

  return div({ class: 'flex flex-wrap gap-2 mt-3' },
    links.map(link => button({
      class: 'btn btn-xs btn-primary gap-1.5 px-3 py-1.5 h-auto min-h-0 normal-case rounded-lg shadow-sm hover:shadow-md transition-all',
      onclick: (e) => {
        e.stopPropagation()
        if (link.onclick) link.onclick()
        else goTo(link.to)
      }
    },
    span({ class: 'material-symbols-rounded text-[14px]' }, link.icon),
    link.label
    ))
  )
}

export const Queue = () => {
  const jobs = fnstate([])
  const jobTypes = fnstate([], t => t)
  const filterStatus = fnstate('')
  const filterType = fnstate('')
  const showDlq = fnstate(false)
  const page = fnstate(0)
  const limit = 50
  const totalCount = fnstate(0)
  const health = fnstate({ queue: { pending: 0, processing: 0, failed: 0 }, gpus: [], db: {}, loading: true })

  const fetchHealth = async () => {
    try {
      const res = await fetch('/api/system/health-stats')
      if (res.ok) {
        const data = await res.json()
        health({ ...data, loading: false })
      }
    } catch (err) {
      console.error('Error fetching health:', err)
    }
  }

  const fetchQueue = async () => {
    const status = filterStatus()
    const type = filterType()
    const dlq = showDlq()
    const offset = page() * limit

    const res = await fetch(`/api/queue?limit=${limit}&offset=${offset}&status=${status}&type=${type}&is_dlq=${dlq}&_=${Date.now()}`)
    if (res.ok) {
      const data = await res.json()
      jobs(data)
      if (data.length > 0) {
        totalCount(parseInt(data[0].total_count))
      } else {
        totalCount(0)
      }
    }
  }

  const handleRetry = async (id) => {
    const res = await fetch(`/api/queue/${id}/retry`, { method: 'POST' })
    if (res.ok) fetchQueue()
  }

  const handleFail = async (id) => {
    if (!await window.confirm('Are you sure you want to kill this job?')) return
    const res = await fetch(`/api/queue/${id}/fail`, { method: 'POST' })
    if (res.ok) fetchQueue()
  }

  const fetchJobTypes = async () => {
    try {
      const res = await fetch('/api/queue/types')
      if (res.ok) jobTypes(await res.json())
    } catch (err) {
      console.error('Error fetching job types:', err)
    }
  }

  fetchQueue()
  fetchHealth()
  fetchJobTypes()

  const root = div({ class: 'p-4 md:p-6 flex flex-col gap-6' },
    div({ class: 'flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4' },
      h1({ class: 'text-2xl md:text-3xl font-bold' }, 'Queue Manager'),
      div({ class: 'flex gap-2 w-full sm:w-auto' },
        button({ class: 'btn btn-outline btn-sm flex-1 sm:flex-none', onclick: () => { fetchQueue(); fetchHealth() } }, 'Refresh Now')
      )
    ),

    health.bindAs(h => {
      if (h.loading) return div({ class: 'flex justify-center p-4' }, span({ class: 'loading loading-spinner loading-md' }))
      return div({ class: 'card bg-base-200 shadow-xl border border-base-300 max-w-md' },
        div({ class: 'card-body p-4' },
          h2({ class: 'text-xs font-black uppercase tracking-widest opacity-50' }, 'Queue Depth'),
          div({ class: 'flex justify-between items-end' },
            div({ class: 'text-2xl font-bold' }, h.queue.pending + h.queue.processing),
            div({ class: 'flex flex-col text-[10px] items-end uppercase font-bold' },
              span({ class: 'text-success' }, `${h.queue.processing} processing`),
              span({ class: 'opacity-50' }, `${h.queue.pending} pending`)
            )
          ),
          progress({ class: 'progress progress-primary w-full mt-2', value: h.queue.processing, max: h.queue.processing + h.queue.pending || 1 })
        )
      )
    }),

    // Tabs
    div({ role: 'tablist', class: 'tabs tabs-lift' },
      input({
        type: 'radio',
        name: 'queue_tabs',
        role: 'tab',
        class: 'tab',
        'aria-label': 'Active Queue',
        checked: showDlq.bindAttr(d => !d),
        onclick: () => {
          showDlq(false)
          page(0)
          fetchQueue()
        }
      }),
      input({
        type: 'radio',
        name: 'queue_tabs',
        role: 'tab',
        class: 'tab',
        'aria-label': 'Dead Letter Queue',
        checked: showDlq.bindAttr(),
        onclick: () => {
          showDlq(true)
          page(0)
          fetchQueue()
        }
      })
    ),
    // Filters
    div({ class: 'flex flex-col sm:flex-row gap-4' },
      select({
        class: 'select select-bordered select-sm w-full sm:w-auto',
        onchange: (e) => {
          filterStatus(e.target.value)
          page(0)
          fetchQueue()
        }
      },
      option({ value: '' }, 'All Statuses'),
      option({ value: 'PENDING' }, 'Pending'),
      option({ value: 'PROCESSING' }, 'Processing'),
      option({ value: 'COMPLETED' }, 'Completed'),
      option({ value: 'FAILED' }, 'Failed')
      ),
      jobTypes.bindChildren(select({
        class: 'select select-bordered select-sm w-full sm:w-auto',
        onchange: (e) => {
          filterType(e.target.value)
          page(0)
          fetchQueue()
        }
      },
      option({ value: '' }, 'All Types')
      ), (typeState) => {
        const t = typeState()
        return option({ value: t }, t.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' '))
      })
    ),
    // Job List
    div({ class: 'flex flex-col' },
      jobs.bindAs(list => {
        if (!list || list.length === 0) {
          return div({ class: 'p-12 text-center opacity-60 bg-base-200 rounded-xl border border-base-300' }, 'No jobs found.')
        }

        const renderStatus = (j) => div({ class: 'flex flex-col items-end shrink-0' },
          span({ class: `badge badge-sm ${j.status === 'FAILED' ? 'badge-error' : j.status === 'COMPLETED' ? 'badge-success' : 'badge-ghost'}` }, j.status)
        )

        const renderError = (j) => j.failed_reason
          ? div({ class: 'bg-error/10 text-error p-3 rounded-lg text-xs break-words border border-error/20' },
            div({ class: 'font-bold mb-1 uppercase text-[10px] opacity-70' }, 'Failure Reason:'),
            j.failed_reason
          )
          : ''

        const renderRuntime = (j) => {
          const created = new Date(j.created_at)
          const updated = new Date(j.updated_at)
          return j.status === 'PROCESSING'
            ? Math.round((Date.now() - created.getTime()) / 1000) + 's'
            : Math.round((updated.getTime() - created.getTime()) / 1000) + 's'
        }

        const renderActions = (j) => div({ class: 'flex gap-2' },
          (j.status === 'COMPLETED' || j.status === 'FAILED')
            ? button({ class: 'btn btn-xs btn-outline flex-grow sm:flex-grow-0', onclick: () => handleRetry(j.id) }, 'Retry')
            : '',
          j.status === 'PROCESSING'
            ? button({ class: 'btn btn-xs btn-error flex-grow sm:flex-grow-0', onclick: () => handleFail(j.id) }, 'Kill')
            : ''
        )

        const renderStatusUpdates = (j) => {
          if (!j.status_updates || j.status_updates.length === 0) return ''
          const current = j.status_updates[0]
          return div({ class: 'flex flex-col gap-2 mt-4' },
            span({ class: 'text-[10px] font-bold uppercase opacity-50 tracking-wider' }, 'Current Progress'),
            div({ class: 'bg-primary/5 p-3 rounded-lg border border-primary/20 text-xs' },
              Object.entries(current).map(([key, value]) => div({ class: 'flex gap-2' },
                span({ class: 'font-bold opacity-60' }, `${key}:`),
                span(typeof value === 'object' ? JSON.stringify(value) : value.toString())
              ))
            )
          )
        }

        return div({ class: 'grid grid-cols-1 lg:grid-cols-2 gap-4' },
          list.map(j => div({ class: 'card bg-base-200 border border-base-300 shadow-sm p-5 flex flex-col gap-4' },
            div({ class: 'flex justify-between items-start gap-4' },
              div({ class: 'flex flex-col min-w-0' },
                div({ class: 'flex items-center gap-2 mb-1' },
                  span({ class: 'text-xs opacity-50 font-bold uppercase tracking-wider' }, `Job #${j.id}`),
                  span({ class: 'text-xs opacity-30' }, '•'),
                  span({ class: 'text-[10px] opacity-50 font-bold uppercase tracking-tight' }, new Date(j.created_at).toLocaleString())
                ),
                span({ class: 'font-bold text-xl truncate' }, j.job_type)
              ),
              renderStatus(j)
            ),
            renderError(j),
            renderStatusUpdates(j),
            div({ class: 'grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm bg-base-300/30 p-3 rounded-lg border border-base-content/5' },
              div({ class: 'flex flex-col' },
                span({ class: 'text-[10px] uppercase font-bold opacity-50 mb-0.5' }, 'Runtime'),
                span({ class: 'font-mono' }, renderRuntime(j))
              ),
              div({ class: 'flex flex-col' },
                span({ class: 'text-[10px] uppercase font-bold opacity-50 mb-0.5' }, 'Attempts'),
                span({ class: 'font-mono' }, j.attempts)
              ),
              div({ class: 'flex flex-col hidden sm:flex' },
                span({ class: 'text-[10px] uppercase font-bold opacity-50 mb-0.5' }, 'Created'),
                span({ class: 'font-mono' }, new Date(j.created_at).toLocaleDateString())
              )
            ),
            div({ class: 'flex flex-col gap-2' },
              span({ class: 'text-[10px] font-bold uppercase opacity-50 tracking-wider' }, 'Job Payload'),
              div({ class: 'bg-base-300/50 p-3 rounded text-[11px] font-mono break-words whitespace-pre-wrap border border-base-content/5 max-h-48 overflow-y-auto' },
                JSON.stringify(j.payload, null, 2)
              ),
              PayloadAnalyzer(j.payload)
            ),
            div({ class: 'border-t border-base-300 pt-4 mt-auto' }, renderActions(j))
          ))
        )
      })
    ),
    // Pagination
    div({ class: 'flex justify-center items-center gap-4' },
      button({
        class: 'btn btn-sm',
        disabled: page.bindAttr(p => p === 0),
        onclick: () => {
          page(page() - 1)
          fetchQueue()
        }
      }, 'Prev'),
      span(page.bindAs(p => `Page ${p + 1}`)),
      button({
        class: 'btn btn-sm',
        disabled: jobs.bindAttr(list => list.length < limit),
        onclick: () => {
          page(page() + 1)
          fetchQueue()
        }
      }, 'Next')
    )
  )

  const handleJobUpdate = () => {
    if (!root.isConnected) {
      eventBus.off('job_update', handleJobUpdate)
      return
    }
    fetchQueue()
  }

  eventBus.on('job_update', handleJobUpdate)

  // Auto refresh every 30s as fallback
  const interval = setInterval(() => {
    if (!root.isConnected) {
      clearInterval(interval)
      return
    }
    fetchQueue()
  }, 30000)

  return root
}
